import { useEffect, useState } from "react";
import { getDocs, collection, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db } from "../../config/firebase";
import { auth } from "../../config/firebase";
import "./orders.css";
import Heading from "../../common/Heading";
import { Link } from "react-router-dom";
import Seo from "../../components/Seo";

import { routeMeta } from "../../seo/staticRoutes";
export default function Orders(props) {
  const [ordersList, setOrdersList] = useState([]);
  const [loading, setLoading] = useState(true);
  const {allOrders}=props


  useEffect(() => {
    // Firebase restores the session asynchronously, so auth.currentUser is null
    // on first render. The old code read it directly and, when it was null, fell
    // through to fetching the ENTIRE orders collection — then filtered on
    // auth.currentUser.email, which could also throw. Wait for auth to settle
    // and never query unscoped unless this is explicitly the admin view.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const email = user?.email;

      if (!allOrders && !email) {
        setOrdersList([]);
        setLoading(false);
        return;
      }

      const ordersRef = allOrders
        ? collection(db, "orders")
        : query(collection(db, "orders"), where("email", "==", email));

      try {
        const data = await getDocs(ordersRef);
        const filteredData = data.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setOrdersList(filteredData);
      } catch (err) {
        console.error("Could not load orders", err);
        setOrdersList([]);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [allOrders]);

  const getStatusColor = (status = 'processing') => {
    switch(status.toLowerCase()) {
      case 'delivered': return '#28a745';
      case 'shipped': return '#007bff';
      case 'processing': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status = 'processing') => {
    switch(status.toLowerCase()) {
      case 'delivered': return 'fas fa-check-circle';
      case 'shipped': return 'fas fa-truck';
      case 'processing': return 'fas fa-clock';
      case 'cancelled': return 'fas fa-times-circle';
      default: return 'fas fa-info-circle';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return new Date().toLocaleDateString('en-IN');
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (<>
    <Seo {...routeMeta("/myorders")} />
    <Heading title="Home" subtitle={!allOrders?"My Orders":"Dashboard"} />

    <div className="professional-orders-container">
      <div className="container py-5">
        {loading ? (
          <div className="text-center py-5">
            <i className="fas fa-spinner fa-spin mr-2"></i>
            <span>Loading your orders…</span>
          </div>
        ) : ordersList && ordersList.length ? (
          <>
            <div className="orders-header">
              <div className="row align-items-center mb-4">
                <div className="col-md-8">
                  <h2 className="orders-title">
                    <i className="fas fa-shopping-bag mr-3"></i>
                    {!allOrders ? 'My Orders' : 'All Orders'}
                  </h2>
                  <p className="orders-subtitle">
                    {!allOrders 
                      ? 'Track and manage your NT Sticks orders'
                      : `Managing ${ordersList.length} total orders`
                    }
                  </p>
                </div>
                <div className="col-md-4 text-right">
                  <div className="orders-stats">
                    <div className="stat-item">
                      <span className="stat-number">{ordersList.length}</span>
                      <span className="stat-label">Total Orders</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="orders-grid">
              {ordersList.map((item, i) => (
                <div className="professional-order-card" key={i}>
                  {/* Order Header */}
                  <div className="order-header">
                    <div className="order-header-left">
                      <div className="order-id">
                        <i className="fas fa-receipt mr-2"></i>
                        Order #{item.id.substring(0, 8).toUpperCase()}
                      </div>
                      <div className="order-date">
                        <i className="fas fa-calendar mr-2"></i>
                        {formatDate(item.timestamp)}
                      </div>
                    </div>
                    <div className="order-header-right">
                      <div className="order-total">
                        ₹{item.orderData?.totalPrice || '0'}
                      </div>
                      <div 
                        className="order-status"
                        style={{ 
                          backgroundColor: getStatusColor(item.status),
                          color: 'white'
                        }}
                      >
                        <i className={`${getStatusIcon(item.status)} mr-1`}></i>
                        {item.status || 'Processing'}
                      </div>
                    </div>
                  </div>

                  {/* Customer Info - Compact Layout */}
                  <div className="customer-info-compact">
                    <div className="customer-summary">
                      <div className="customer-main">
                        <div className="customer-details">
                          <h6 className="customer-name">
                            <i className="fas fa-user mr-2"></i>
                            {item.email}
                          </h6>
                          <div className="contact-info">
                            <span className="phone-number">
                              <i className="fas fa-phone mr-1"></i>
                              {item.phone}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="delivery-address">
                        <div className="address-header">
                          <i className="fas fa-map-marker-alt mr-2"></i>
                          <span className="address-label">Delivery Address</span>
                        </div>
                        <p className="address-text">{item.Address}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Items - Simplified */}
                  <div className="order-items-compact">
                    <div className="items-summary">
                      <h6 className="items-count">
                        <i className="fas fa-box mr-2"></i>
                        {item.orderData?.itemsList?.length || 0} Items Ordered
                      </h6>
                      <div className="items-quick-list">
                        {item.orderData?.itemsList?.map((orderItem, index) => (
                          <div className="item-compact" key={index}>
                            <span className="item-name">{orderItem.itemName}</span>
                            <span className="item-qty">×{orderItem.quantity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Order Actions */}
                  <div className="order-actions">
                    <button className="action-btn track-btn">
                      <i className="fas fa-truck mr-2"></i>
                      Track Order
                    </button>
                    <button className="action-btn support-btn">
                      <i className="fas fa-headset mr-2"></i>
                      Support
                    </button>
                    {allOrders && (
                      <button className="action-btn manage-btn">
                        <i className="fas fa-edit mr-2"></i>
                        Manage
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="empty-orders-state">
            <div className="empty-icon">
              <i className="fas fa-shopping-bag"></i>
            </div>
            <h3 className="empty-title">No Orders Found</h3>
            <p className="empty-subtitle">
              {!allOrders 
                ? "You haven't placed any orders yet. Start shopping for premium incense products!"
                : "No orders have been placed yet."
              }
            </p>
            {!allOrders && (
              <Link to="/shop" className="btn btn-primary-custom">
                <i className="fas fa-store mr-2"></i>
                Start Shopping
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
